import './mailCodeOfferPage.scss';

import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import { ReactNode, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import vehicleSUVTopView from '../../../assets/vehicle-suv-topview.png';
import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { LanderImage } from '../../../components/LanderImage/LanderImage';
import { MailCodeSavingsOffer } from '../../../components/MailCodeSavingsOffer/MailCodeSavingsOffer';
import { Rate } from '../../../components/Rate/Rate';
import { MailCodeOfferStatus } from '../../../store/mailCodeStore/enums/MailCodeOfferStatus';
import { MailCodeOfferType } from '../../../store/mailCodeStore/enums/MailCodeOfferType';
import useMailCodeStore from '../../../store/mailCodeStore/useMailCodeStore';
import usePinStore from '../../../store/pinStore/usePinStore';
import { useNavigateWithCallback } from '../../../store/routeStore/hooks/useNavigateWithCallback';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();
  const navigateWithCallback = useNavigateWithCallback();

  const { t } = useTranslation();

  const {
    computed: {
      applicantFirstName,
      offerDisplayValues: {
        currentPayment,
        newPayment,
        newRate,
        newTerm,
        potentialSavings,
        preApprovalAmount,
      },
      offerStatus,
      offerStatusRoute,
      offerType,
    },
  } = useMailCodeStore();

  const { handleSubmit } = useForm();

  const formSubmit = (): void => {
    navigateWithCallback('/mail/activation').then((): void => {
      usePinStore.setState({
        deliverPinSuccess: false,
      });
    });
  };

  useEffect((): void => {
    if (offerStatus !== MailCodeOfferStatus.COMPLETE) {
      navigate(offerStatusRoute, { replace: true });
    }
  }, [offerStatus, offerStatusRoute]);

  return (
    <div className="mail-code-offer-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <Header showLogo />

          <main>
            <LanderImage
              alt={t('imageAlts.vehicleSUVTopView')}
              defaultImage={vehicleSUVTopView}
            />
            <form
              data-testid={locator.pages.mailCodeOffer.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.mailCodeOffer.form.h1}>
                  {t(`MailCodeOfferPage.title.${offerType}`, {
                    firstName: applicantFirstName,
                  })}
                </h1>
                <h2 data-testid={locator.pages.mailCodeOffer.form.h2}>
                  {t('MailCodeOfferPage.subtitle', {
                    amount: preApprovalAmount,
                  })}
                </h2>

                <div style={{ marginBottom: '3rem' }}>
                  {offerType === MailCodeOfferType.RATE ? (
                    <Rate
                      apr={newRate}
                      monthlyPaymentAmount={newPayment}
                      term={newTerm}
                      isEstimate
                    />
                  ) : (
                    <MailCodeSavingsOffer
                      currentPayment={currentPayment}
                      newPayment={newPayment}
                      potentialSavings={potentialSavings}
                    />
                  )}
                </div>

                <Button
                  data-testid={locator.pages.mailCodeOffer.form.submitButton}
                  fullWidth
                  submit
                >
                  {t('MailCodeOfferPage.continue')}
                </Button>
              </div>
              <footer data-testid={locator.pages.mailCodeOffer.footer.root}>
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'MailCodeOfferPage';
